import { useEffect, useRef, useState } from "react";

interface DeferredLoadingOptions {
  /** Wait this long before showing anything, so a fast response never flashes an overlay. */
  showDelayMs: number;
  /** Once shown, stay up at least this long, so the overlay never blinks straight back out. */
  minVisibleMs: number;
}

/**
 * Turns a raw `isFetching` flag into one that is safe to drive a full-surface overlay with.
 *
 * A cached response can land in ~100ms. Rendering an overlay for 100ms is worse than rendering
 * nothing: the eye catches the flash without ever reading it. The two guards below are what make
 * an overlay acceptable on a surface whose responses vary from ~100ms (cached) to several
 * seconds (cold 180-day window):
 *
 * - `showDelayMs` — nothing is shown until the request has been in flight this long. Most
 *   period switches inside the cache window therefore show nothing at all.
 * - `minVisibleMs` — once shown, it stays for a minimum duration even if the data arrives
 *   immediately after.
 *
 * Both timers are cleared on unmount, and re-entrancy is handled: flipping `active` back on
 * while a hide is pending cancels the hide rather than queueing a second show.
 */
export default function useDeferredLoading(
  active: boolean,
  { showDelayMs, minVisibleMs }: DeferredLoadingOptions
): boolean {
  const [visible, setVisible] = useState(false);
  const shownAtRef = useRef<number | null>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (active) {
      // A new fetch started while we were waiting out the minimum — keep the overlay up rather
      // than letting the pending hide tear it down under the next request.
      clearTimeout(hideTimerRef.current);
      if (shownAtRef.current != null) return undefined;

      showTimerRef.current = setTimeout(() => {
        shownAtRef.current = Date.now();
        setVisible(true);
      }, showDelayMs);

      return () => clearTimeout(showTimerRef.current);
    }

    clearTimeout(showTimerRef.current);

    // Never became visible — the response beat the show delay, which is the common case.
    if (shownAtRef.current == null) {
      setVisible(false);
      return undefined;
    }

    const elapsed = Date.now() - shownAtRef.current;
    hideTimerRef.current = setTimeout(
      () => {
        shownAtRef.current = null;
        setVisible(false);
      },
      Math.max(0, minVisibleMs - elapsed)
    );

    return () => clearTimeout(hideTimerRef.current);
  }, [active, showDelayMs, minVisibleMs]);

  useEffect(
    () => () => {
      clearTimeout(showTimerRef.current);
      clearTimeout(hideTimerRef.current);
    },
    []
  );

  return visible;
}
